import { create } from 'zustand'
import { supabase } from '@/lib/supabase'
import type { Notification, NotificationPreferences, NotificationPreferencesFormData } from '@/types/notifications'

interface NotificationState {
  notifications: Notification[]
  preferences: NotificationPreferences | null
  loading: boolean
  error: string | null
  unreadCount: number

  fetchNotifications: (userId: string) => Promise<void>
  fetchPreferences: (userId: string) => Promise<void>
  createNotification: (userId: string, type: Notification['type'], title: string, message: string, data?: Record<string, any>) => Promise<void>
  markAsRead: (notificationId: string) => Promise<void>
  markAllAsRead: (userId: string) => Promise<void>
  deleteNotification: (notificationId: string) => Promise<void>
  clearAllNotifications: (userId: string) => Promise<void>
  updatePreferences: (userId: string, data: NotificationPreferencesFormData) => Promise<void>
  initializePreferences: (userId: string) => Promise<void>
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  preferences: null,
  loading: false,
  error: null,
  unreadCount: 0,

  fetchNotifications: async (userId: string) => {
    set({ loading: true, error: null })
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50)

      if (error) throw error

      // @ts-ignore - Supabase type inference issue
      const unreadCount = data?.filter((n) => !n.read).length || 0
      set({ notifications: data || [], unreadCount, loading: false })
    } catch (error) {
      set({ error: (error as Error).message, loading: false })
    }
  },

  fetchPreferences: async (userId: string) => {
    set({ loading: true, error: null })
    try {
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', userId)
        .single()

      if (error && error.code !== 'PGRST116') throw error // PGRST116 = no rows returned

      set({ preferences: data || null, loading: false })
    } catch (error) {
      set({ error: (error as Error).message, loading: false })
    }
  },

  createNotification: async (userId: string, type: Notification['type'], title: string, message: string, data?: Record<string, any>) => {
    try {
      const { error } = await supabase
        .from('notifications')
        // @ts-ignore - Supabase type inference issue with Insert types
        .insert([{
          user_id: userId,
          type,
          title,
          message,
          data: data || null,
          read: false,
        }])

      if (error) throw error

      // Refresh notifications
      await get().fetchNotifications(userId)
    } catch (error) {
      console.error('Error creating notification:', error)
    }
  },

  markAsRead: async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        // @ts-ignore - Supabase type inference issue with Update types
        .update({ read: true })
        .eq('id', notificationId)

      if (error) throw error

      set((state) => ({
        notifications: state.notifications.map((n) =>
          n.id === notificationId ? { ...n, read: true } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }))
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  },

  markAllAsRead: async (userId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        // @ts-ignore - Supabase type inference issue with Update types
        .update({ read: true })
        .eq('user_id', userId)
        .eq('read', false)

      if (error) throw error

      set((state) => ({
        notifications: state.notifications.map((n) => ({ ...n, read: true })),
        unreadCount: 0,
      }))
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
    }
  },

  deleteNotification: async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId)

      if (error) throw error

      set((state) => {
        const notification = state.notifications.find((n) => n.id === notificationId)
        const unreadCount = notification && !notification.read
          ? Math.max(0, state.unreadCount - 1)
          : state.unreadCount

        return {
          notifications: state.notifications.filter((n) => n.id !== notificationId),
          unreadCount,
        }
      })
    } catch (error) {
      console.error('Error deleting notification:', error)
    }
  },

  clearAllNotifications: async (userId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', userId)

      if (error) throw error

      set({ notifications: [], unreadCount: 0 })
    } catch (error) {
      console.error('Error clearing notifications:', error)
    }
  },

  updatePreferences: async (userId: string, data: NotificationPreferencesFormData) => {
    set({ loading: true, error: null })
    try {
      const { data: preferences, error } = await supabase
        .from('notification_preferences')
        // @ts-ignore - Supabase type inference issue with Upsert types
        .upsert([{ user_id: userId, ...data }], { onConflict: 'user_id' })
        .select()
        .single()

      if (error) throw error

      set({ preferences, loading: false })
    } catch (error) {
      set({ error: (error as Error).message, loading: false })
    }
  },

  initializePreferences: async (userId: string) => {
    try {
      // Check if preferences exist
      const { data: existing } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', userId)
        .single()

      if (!existing) {
        // Create default preferences
        const { data, error } = await supabase
          .from('notification_preferences')
          // @ts-ignore - Supabase type inference issue with Insert types
          .insert([{
            user_id: userId,
            enabled: true,
            reminder_time: 60,
            daily_summary: true,
            low_stock: true,
            achievement_unlocks: true,
            browser_notifications: true,
          }])
          .select()
          .single()

        if (error) throw error
        set({ preferences: data })
      } else {
        set({ preferences: existing })
      }
    } catch (error) {
      console.error('Error initializing preferences:', error)
    }
  },
}))
